import { useQuery } from '@apollo/client';
import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import * as styles from './ExpandedPostModal.module.scss';
import ExpandedPostModalMagneticWall from './ExpandedPostModalMagneticWall';
import ExpandedPostModalTopicBar from './ExpandedPostModalTopicBar';
import PostModalView from './PostModalView';
import Timestamp from '../../components/Timestamp';
import { withQuillContextProvider } from '../../editor/QuillContext';
import type QuillEditor from '../../editor/QuillEditor';
import { gql } from '../../graphql/generated';
import postPath from '../../utils/url/postPath';
import ControlBarContributorsButton from '../controlBar/ControlBarContributorsButton';

const POST_QUERY = gql(/** @complexity 23 */ `
  query ExpandedPostModal_post($id: ID!) {
    post(id: $id) {
      updatedAt
      externalPost {
        id
      }
      ...PostModalView_post
      ...ControlBarContributorsButton_post
      ...ExpandedPostModalTopicBar_post
      ...ExpandedPostModalMagneticWall_post
      ...postPath_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...PostModalView_entityAssetToken
    }
  }
`);

type ExpandedPostModalProps = Omit<
  ComponentProps<typeof PostModalView>,
  'onClose' | 'post' | 'entityAssetToken' | 'headerBar'
> & {
  id: string;
  close: () => void;
};

const ExpandedPostModal = ({ id, close, ...props }: ExpandedPostModalProps) => {
  const [quill, setQuill] = useState<QuillEditor | null>(null);

  const { data } = useQuery(POST_QUERY, { variables: { id } });
  const { post, entityAssetToken } = data ?? {};

  return (
    <PostModalView
      {...props}
      onClose={close}
      post={post}
      entityAssetToken={entityAssetToken}
      headerBar={
        post &&
        entityAssetToken && (
          <div className={styles.headerBar}>
            <div className={styles.controlBarContainer}>
              <ControlBarContributorsButton post={post} interactionsDisabled>
                {post.externalPost ? 'Synced' : 'Edited'}{' '}
                <Timestamp date={post.updatedAt} />
              </ControlBarContributorsButton>
            </div>
            <div className={styles.topicBarContainer}>
              <ExpandedPostModalTopicBar post={post} />
            </div>
          </div>
        )
      }
      magneticWall={
        quill &&
        post && <ExpandedPostModalMagneticWall quill={quill} post={post} />
      }
      onQuillMount={setQuill}
      newTabTo={post ? postPath(post) : undefined}
    />
  );
};

export default withQuillContextProvider(ExpandedPostModal);
